import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzInputModule } from 'ng-zorro-antd/input'; // <-- Imported for proper dark mode input mapping

import { ProjectControllerService } from '@rapider/sdk';
import { NotificationService } from '@rapider/services';
import { AuthStore } from '@rapider/authentication';
import {
  WORKSPACE_CONFIG,
  CODE_GENERATION_TEMPLATES,
  isDeleteKeyMatch,
  toggleCodeTemplateId
} from '../workspace.config';

/**
 * @class WorkspaceSettingsComponent
 * @description Provides the interface for users to read metadata, configure features, manage members, and delete workspaces.
 */
@Component({
  selector: 'rapider-workspace-settings',
  standalone: true,
  // Added NzInputModule to the imports array
  imports: [CommonModule, FormsModule, NzSwitchModule, NzTableModule, NzTagModule, NzInputModule],
  templateUrl: './workspace-settings.component.html',
  styleUrls: ['./workspace-settings.component.scss'],
  host: {
    class: 'block h-full w-full bg-body-background'
  }
})
export class WorkspaceSettingsComponent implements OnInit {
  private readonly projectController = inject(ProjectControllerService);
  private readonly notificationService = inject(NotificationService);
  public readonly authStore = inject(AuthStore);
  private readonly router = inject(Router);

  public readonly config = WORKSPACE_CONFIG;
  public readonly templatesDefinition = CODE_GENERATION_TEMPLATES;

  // Active Data Stream
  public activeProject = this.authStore.activeProject;

  // Editable Form Signals
  public editName = signal<string>('');
  public editDescription = signal<string>('');
  public hasAuth = signal<boolean>(false);
  public hasPayment = signal<boolean>(false);
  public codeTemplateIds = signal<string[]>([]);
  public deleteConfirmKey = signal<string>('');

  // UI State Signals
  public isSavingGeneral = signal<boolean>(false);
  public isSavingModules = signal<boolean>(false);
  public isSavingTemplates = signal<boolean>(false);
  public isDeleting = signal<boolean>(false);

  // Mock Members Data (To be replaced with actual API data)
  public workspaceMembers = signal<any[]>([]);

  ngOnInit(): void {
    this.initializeFormData();
  }

  /**
   * Reads data from the active project signal and seeds the local editable form signals.
   */
  private initializeFormData(): void {
    const project = this.activeProject();
    if (project) {
      this.editName.set(project.name || '');
      this.editDescription.set(project.description || '');
      this.hasAuth.set(!!project.hasAuthenticationModule);
      this.hasPayment.set(!!project.hasPaymentModule);
      this.codeTemplateIds.set(project.codeGenerationTemplateIds || []);

      // Seed a mock owner member for UI demonstration
      this.workspaceMembers.set([
        { id: project.createdById, name: project.createdBy, role: 'Owner', status: 'Active' }
      ]);
    }
  }

  /**
   * Helper function to abstract out the update API call.
   * @param {any} body - Partial project payload to patch.
   * @param {any} loadingSignal - The signal to toggle during the request.
   */
  private updateProjectSlice(body: any, loadingSignal: any): void {
    const projectId = this.activeProject()?.id;
    if (!projectId) return;

    loadingSignal.set(true);

    this.projectController.updateById({ id: projectId, body }).subscribe({
      next: () => {
        loadingSignal.set(false);
        this.notificationService.createNotification('success', 'Success', this.config.notifications.updateSuccess);

        // Update AuthStore local active project reference to reflect changes
        const updatedProject = { ...this.activeProject(), ...body };
        this.authStore.setActiveWorkspace(updatedProject);
      },
      error: () => {
        loadingSignal.set(false);
        this.notificationService.createNotification('error', 'Error', this.config.notifications.updateFailed);
      }
    });
  }

  /** Saves the name and description */
  public saveGeneralSettings(): void {
    this.updateProjectSlice({
      name: this.editName().trim(),
      description: this.editDescription().trim()
    }, this.isSavingGeneral);
  }

  /** Saves the toggle states of application modules */
  public saveModulesSettings(): void {
    this.updateProjectSlice({
      hasAuthenticationModule: this.hasAuth(),
      hasPaymentModule: this.hasPayment()
    }, this.isSavingModules);
  }

  /** Checks if a template ID is currently active based on local state */
  public isTemplateActive(templateId: string): boolean {
    return this.codeTemplateIds().includes(templateId);
  }

  /** Handles the NzSwitch change for a specific template */
  public onTemplateToggle(templateId: string, isActive: boolean): void {
    const newIds = toggleCodeTemplateId(templateId, this.codeTemplateIds(), isActive);
    this.codeTemplateIds.set(newIds);
  }

  /** Commits the chosen templates to the backend */
  public saveTemplatesSettings(): void {
    this.updateProjectSlice({
      codeGenerationTemplateIds: this.codeTemplateIds()
    }, this.isSavingTemplates);
  }

  /** Validation helper for delete button */
  public canDelete(): boolean {
    const project = this.activeProject();
    if (!project) return false;
    return isDeleteKeyMatch(project.key, this.deleteConfirmKey());
  }

  /** Copies the Workspace ID to clipboard */
  public copyWorkspaceId(id: string): void {
    navigator.clipboard.writeText(id).then(() => {
      this.notificationService.createNotification('success', 'Success', this.config.notifications.idCopiedSuccess);
    });
  }

  /** Executes the hard delete command and navigates out. */
  public deleteWorkspace(): void {
    if (!this.canDelete()) return;

    const projectId = this.activeProject()?.id;
    if (!projectId) return;

    this.isDeleting.set(true);

    this.projectController.deleteById({ projectId }).subscribe({
      next: () => {
        this.isDeleting.set(false);
        this.notificationService.createNotification('success', 'Success', this.config.notifications.deleteSuccess);

        // Clear active store and navigate to selection
        this.authStore.setActiveWorkspace(null as any);
        this.router.navigate(['/select-workspace']);
      },
      error: () => {
        this.isDeleting.set(false);
        this.notificationService.createNotification('error', 'Error', this.config.notifications.deleteFailed);
      }
    });
  }
}